<template>
  <div class="field box py-1">
    <div class="field-label is-normal is-flex is-justify-content-space-between is-align-items-center m-0 mb-3 is-flex-direction-row-reverse">
      <button v-if="submitableButton && edit" class="button is-info" @click="handleChange">Submit</button>
      <label class="label">
        {{ label }}
        <span v-if="showNotDisplayed && isLiteMode" class="red_text">&nbsp;</span>
      </label>
    </div>
    <div v-show="edit" class="control ql-container-custom">
      <quill v-model="text" output="html" :config="quillConfig" ref="quillEditor" @input="handleTextChange" />
    </div>
    <div v-show="!edit" @click="updateEdit">
      <div dir="rtl" v-if="text && text.trim().length > 0">
        <div class="is-clickable white_space_pre_line break_word has-text-link" v-html="$sanitize(text)"></div>
      </div>
      <div v-else class="is-clickable has-text-link">
        {{ placeholderText }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'RegularAbstract',
  props: {
    value: {
      type: [String],
      validator: prop => typeof prop === 'string' || prop === null
    },
    applicantId: {
      type: Number,
      required: true
    },
    label: {
      type: String,
      default: 'Abstract'
    },
    submitableButton: {
      type: Boolean,
      default: true
    },
    isEditable: {
      type: Boolean,
      default: false
    },
    showNotDisplayed: {
      type: Boolean,
      default: false
    },
    isLiteMode: {
      type: Boolean,
      default: false
    },
    placeholderText: {
      type: String,
      default: 'Create...'
    }
  },
  data () {
    return {
      edit: false,
      text: '',
      quillConfig: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ direction: 'rtl' }]
          ]
        }
      }
    }
  },
  watch: {
    text (newVal) {
      this.$emit('input', newVal)
    },
    value: {
      handler (newVal) {
        if (newVal !== this.text) {
          this.text = newVal || ''
        }
      },
      immediate: true
    }
  },
  created () {
    this.text = this.value || ''
    this.edit = this.isEditable
    this.addQuillStyling()
  },
  mounted () {
    if (this.value && this.value !== this.text) {
      this.text = this.value
    }

    this.$nextTick(() => {
      if (this.$refs.quillEditor && this.$refs.quillEditor.$el) {
        this.setDefaultAlignRight()
      }
    })
  },
  beforeDestroy () {
    this.detachQuillBlurEvent()
  },
  methods: {
    addQuillStyling () {
      var link = document.createElement('link')
      link.setAttribute('rel', 'stylesheet')
      link.setAttribute('href', 'https://cdn.quilljs.com/1.2.6/quill.snow.css')
      document.body.appendChild(link)
    },
    updateEdit () {
      this.edit = true
      this.$nextTick(() => {
        if (this.$refs.quillEditor && this.$refs.quillEditor.$el) {
          this.setDefaultAlignRight()
        }
      })
    },
    setDefaultAlignRight () {
      try {
        const pElement = this.$refs.quillEditor.$el.querySelector('p')
        const containerElement = this.$refs.quillEditor.$el.querySelector('.ql-container')

        if (pElement) {
          pElement.classList.add('ql-align-right')
          pElement.classList.add('ql-direction-rtl')
        }
        if (containerElement) {
          containerElement.classList.add('ql-container-custom')
        }
      } catch (error) {
        console.warn('Could not set RTL alignment:', error)
      }
    },
    attachQuillBlurEvent () {
      const quillEditor = this.$refs.quillEditor && this.$refs.quillEditor.editor
      if (quillEditor) {
        quillEditor.on('selection-change', (range) => {
          if (range === null) {
            this.handleChange()
          }
        })
      }
    },
    detachQuillBlurEvent () {
      const quillEditor = this.$refs.quillEditor && this.$refs.quillEditor.editor
      if (quillEditor) {
        quillEditor.off('selection-change')
      }
    },
    async handleTextChange (newText) {
      this.text = newText
      this.$emit('input', newText)

      // Auto-save regular abstract
      const trimmedText = newText ? newText.trim() : ''
      if (trimmedText.length === 0) {
        return // Don't save empty content
      }

      await this.saveRegularAbstract(newText)
    },
    async saveRegularAbstract (text) {
      try {
        const trimmedText = text ? text.trim() : ''
        if (trimmedText.length === 0) {
          console.log('Skipping save of empty regular abstract')
          return
        }

        await this.$network_utils.updateApplicant({
          applicant_id: this.applicantId,
          abstract: text,
          active_at: new Date()
        })
      } catch (e) {
        console.error('Failed to update regular abstract:', e)
        this.$utils.emit('show_toast', { text: 'שגיאה בשמירת האבסטרקט' })
      }
    },
    async handleChange () {
      this.edit = false
      this.handleStatCreation()
      this.$utils.emit('show_toast', { text: 'Success' })
    },
    handleStatCreation () {
      this.$network_utils.postStat({
        title: 'new internal note',
        designator: 'internal_notes',
        content: this.text,
        status: 'done',
        owner: this.$utils.getOwner().name,
        priority: 7,
        resource_id: this.applicantId,
        resource_type: 'Applicant'
      })
    }
  }
}
</script>

<style scoped>
.ql-container-custom {
  font-size: 17px !important;
}

.red_text {
  color: #ff3860;
}
</style>
